import React from 'react';
import styled from 'styled-components/macro';

import { Box } from '../SubComponents/Modals';
import { Image } from '../SubComponents/Media';
import { Text, Heading2, BigLabel, Label } from '../SubComponents/Text';
import { IconButton, ModalButton } from '../SubComponents/Buttons';
import { ReactComponent as Minus } from '../Assets/Icons/minus.svg';
import { ReactComponent as Plus } from '../Assets/Icons/plus.svg';
import useFormatPrice from '../Hooks/use-format-price';
import useAddItem from '../Hooks/use-add-item';
import { getItemImageUrl } from '../Utils/items';
import { ItemType } from './Items';
import useTypeSafeLocalize from 'Hooks/use-type-safe-localize';
import { LocalizationCategory, ProductLocalization } from 'localizations/types';

const ItemWrapper = styled(Box)`
  margin-bottom: 16px;
  max-width: calc(33.3333% - 8px);
  flex: 0 1 calc(33.3333% - 8px);
  @media screen and (max-width: 767px) {
    max-width: calc(50% - 8px);
    flex: 0 1 calc(50% - 8px);
  }
`;

const ItemImage = styled(Image)`
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
`;

const ItemImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  overflow-y: hidden;
  @media screen and (max-width: 767px) {
    height: 150px;
  }
`;

const HeadingWrapper = styled.div`
  width: 100%;
  height: 40px;
  text-align: center;
  padding: 0 5%;
`;

const DescriptionWrapper = styled.div`
  width: 100%;
  height: 35px;
  text-align: center;
  background-color: #f1f3f6;
  padding: 0 5%;
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow-x: hidden;
    max-height: 35px;
  }
`;

const ItemCount = styled.div`
  width: 100%;
  height: 35px;
  text-align: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const ItemPrice = styled.div`
  width: 100%;
  height: 35px;
  text-align: center;
`;

const ItemButton = styled(ModalButton)`
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

interface ItemProps {
  item: {
    id: string;
    name: string;
    shortDescription?: string | null;
    price: number;
    stock?: number | null;
    imagepath?: string | null;
    alcoholUnits?: number;
    rentalItem?: boolean | null;
    rentalStock?: number | null;
  };
  onClick: () => void;
  onChangeItemQuantity?: (item: ItemType, quantity: number) => void;
  lazyload?: boolean;
}

const Item = (props: ItemProps) => {
  const localize = useTypeSafeLocalize();
  const formatPrice = useFormatPrice();

  const imagePath = getItemImageUrl(props.item.imagepath, 330);

  const { addItem, loading, error, itemCount, setItemCount } = useAddItem({
    itemId: props.item.id,
    alcoholUnits: props.item.alcoholUnits,
  });

  return (
    <>
      <ItemWrapper
        flexDirection="column"
        alignItems="center"
        position="relative"
        p="0"
        pb="35px"
        // ml="3"
        mb="3"
      >
        <ItemImageWrapper onClick={props.onClick}>
          <ItemImage alt={props.item.name} src={imagePath} lazyload={props.lazyload} />
        </ItemImageWrapper>
        <HeadingWrapper>
          <Heading2 lineHeight="40px">{props.item.name}</Heading2>
        </HeadingWrapper>
        <DescriptionWrapper>
          <Text lineHeight="35px">{props.item.shortDescription}</Text>
        </DescriptionWrapper>
        <ItemCount>
          <IconButton
            onClick={() => {
              if (itemCount > 1) {
                setItemCount(itemCount - 1);
                if (props.onChangeItemQuantity) {
                  props.onChangeItemQuantity(props.item, itemCount - 1);
                }
              }
            }}
            icon={Minus}
            altText="Close"
            width="35px"
            height="35px"
            p="10px"
          />
          <BigLabel mx="20px" lineHeight="35px">
            {itemCount}
          </BigLabel>
          <IconButton
            onClick={() => {
              if (props.item.rentalItem && props.item.rentalStock) {
                if (itemCount < props.item.rentalStock) {
                  setItemCount(itemCount + 1);
                }
              } else if (props.item.stock) {
                if (itemCount < props.item.stock) {
                  setItemCount(itemCount + 1);
                  if (props.onChangeItemQuantity) {
                    props.onChangeItemQuantity(props.item, itemCount + 1);
                  }
                }
              } else {
                setItemCount(itemCount + 1);
                if (props.onChangeItemQuantity) {
                  props.onChangeItemQuantity(props.item, itemCount + 1);
                }
              }
            }}
            icon={Plus}
            altText="Close"
            width="35px"
            height="35px"
            p="10px"
          />
        </ItemCount>
        <ItemPrice>
          <Label lineHeight="35px">{formatPrice(props.item.price)}</Label>
        </ItemPrice>
        <ItemButton onClick={addItem} isLoading={loading}>
          {localize(LocalizationCategory.product, ProductLocalization.addToOrder)}
        </ItemButton>
        {error ? (
          <Label error ml="12px" mb="12px">
            {error}
          </Label>
        ) : null}
      </ItemWrapper>
    </>
  );
};

export default Item;
